// Boards and streams — zod schemas (docs/plans/2026-10-02-borden-ontwerp.md §3.6).
// Only this file imports from ../validators (see the import-order rule).

import { z } from 'zod'
import { WorkflowKeySchema } from '../validators/index.js'
import { PROJECT_KEY_RE, TICKET_PREFIX_RE } from './defaults.js'

export const QueueLaneSchema = z.enum(['inbox', 'backlog', 'ready'])
export const SeveritySchema = z.enum(['S1', 'S2', 'S3', 'S4'])
export const StreamOrderRuleSchema = z.enum(['manual', 'severity_age', 'priority_age'])
export const DefinitionOfReadyCheckSchema = z.enum(['acceptance_criteria', 'no_open_blockers', 'severity_set'])
export const StreamColorSchema = z.enum(['moss', 'info', 'signal', 'danger', 'ok', 'ink'])
export const TaskLinkKindSchema = z.enum(['blocks', 'relates', 'duplicate_of', 'spawned_from'])

export const StreamLanesSchema = z
  .object({ inbox: z.boolean(), backlogReady: z.boolean() })
  .refine((l) => l.inbox || l.backlogReady, 'A stream needs the Inbox or Backlog + Ready lanes')
export const StreamExpediteSchema = z.object({ severity: z.literal('S1'), overWip: z.number().int().min(0).max(3) })

export const UpdateStreamRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    color: StreamColorSchema.optional(),
    ticketPrefix: z.string().regex(TICKET_PREFIX_RE).optional(),
    /** null (or empty) falls back to the derived caption. */
    newTaskLabel: z
      .string()
      .trim()
      .max(40)
      .nullable()
      .optional()
      .transform((v) => (v === '' ? null : v)),
    position: z.number().int().min(0).max(999).optional(),
    lanes: StreamLanesSchema.optional(),
    order: StreamOrderRuleSchema.optional(),
    wipLimit: z.number().int().min(1).max(100).nullable().optional(),
    countHumanSteps: z.boolean().optional(),
    autoPull: z.boolean().optional(),
    paused: z.boolean().optional(),
    expedite: StreamExpediteSchema.nullable().optional(),
    weeklyBudgetCents: z.number().int().min(0).max(100_000_000).nullable().optional(),
    definitionOfReady: z.array(DefinitionOfReadyCheckSchema).max(10).optional(),
  })
  .strict()

export const QueueMoveRequestSchema = z.object({
  lane: QueueLaneSchema,
  afterTaskId: z.string().uuid().nullable().optional(),
  beforeTaskId: z.string().uuid().nullable().optional(),
  force: z.boolean().optional(),
  unpin: z.boolean().optional(),
})
export const SubmitTaskRequestSchema = z.object({ overrideWip: z.boolean().optional() }).default({})
export const MoveStreamRequestSchema = z.object({
  workflowKey: WorkflowKeySchema,
  reason: z.string().trim().max(2000).nullable().optional(),
})
export const CreateTaskLinkRequestSchema = z.object({ kind: TaskLinkKindSchema, taskId: z.string().uuid() })
export const ProjectKeySchema = z.string().regex(PROJECT_KEY_RE, 'Use 2-10 capital letters or digits, starting with a letter')
